import { getSessionReplayAmplitudeApiKey, debugEnabled } from '../../config';
import { analyticsClient } from '../analyticsClient';
import { logError } from '../../utils/logger';

/**
 * Dynamic imports of `@amplitude/session-replay-browser` use `webpackChunkName` magic comments so the
 * bundler can emit a stable async chunk. **Atlaspack respects these** when built with
 * `supportWebpackChunkName` (see `scripts/atlaspack-build.js` in extension-frontend).
 */

/**
 * UGC filter rules for session replay (required if Heatmaps are enabled).
 * Redact URLs to avoid recording sensitive paths. Aligned with Atlassian integration guide.
 */
const UGC_FILTER_RULES = [
	{
		selector: 'https://*.atlassian.net/browse/*',
		replacement: 'https://REDACTED.atlassian.net/browse/REDACTED',
	},
	{ selector: 'https://*', replacement: 'https://REDACTED' },
	{ selector: 'http://*', replacement: 'http://REDACTED' },
];

/**
 * NTP (New Tab Page) UGC privacy: mask or block user-identifiable and user-generated content in session replay.
 *
 * All widgets (shortcuts, recent work, announcements) get the same treatment:
 * - Block only images (favicons, avatars, icons) → grey in replay.
 * - Mask text (titles, metadata) → starred in replay.
 *
 * Profile (top right): container text masked, profile picture blocked (grey).
 *
 * Chat input area (uploaded files, chat mode options, prompt text): entire area masked (text starred),
 * any file preview images blocked (grey).
 *
 * `body` is included last as an Atlassian session-replay integration failsafe: when the effective
 * `defaultMaskLevel` is not `conservative` (e.g. remote `sr_privacy_config` override), the SDK falls
 * back to `maskSelector` only without `maskTextSelector: '*'`. `body` then keeps text masked broadly.
 *
 * **Root `amp-mask`:** `public/index.html` sets `class="amp-mask"` on `#root` (same class as
 * {@link SessionReplayUGC}). rrweb only runs `maskTextFn` on text when `needMaskingText` passes; with
 * conservative config the SDK sets `maskTextSelector: '*'`, and `closest('*')` can fail inside rrweb’s
 * try/catch so static text is skipped while `maskAllInputs` still masks typing. A real `.amp-mask`
 * ancestor fixes the gate for the whole app on first paint and async-loaded NTP content.
 */
const NTP_PRIVACY_MASK_SELECTORS = [
	"[data-testid='search-input']",
	"[data-testid='full-chat-input']",
	/** Chat box: prompt text, uploaded file names, chat mode menu labels (reasoning, web search, etc.) */
	"[data-testid='rovo-chat-box-wrapper']",
	"[data-testid='chat-input-wrapper']",
	"[data-testid='welcome-header-container']",
	"[data-testid='profile-container']",
	"[data-testid='empty-shortcuts-container']",
	/** Shortcuts: star titles */
	"[data-testid='ntp-shortcuts-section']",
	/** All dismissible widgets (recent work, announcements): star text */
	"[data-testid='ntp-dismissible-widgets']",
	/** Failsafe — see module comment above */
	'body',
];

const NTP_PRIVACY_BLOCK_SELECTORS = [
	'.bookmarks_container',
	/** Shortcuts: grey favicons only */
	"[data-testid='ntp-shortcuts-section'] img",
	/** All widgets: grey images only (avatars, icons, favicons in recent work, announcements) */
	"[data-testid='ntp-dismissible-widgets'] img",
	/** Profile (top right): grey profile picture only */
	"[data-testid='profile-container'] img",
	/** Chat box: grey file preview / thumbnail images only */
	"[data-testid='rovo-chat-box-wrapper'] img",
	"[data-testid='chat-input-wrapper'] img",
];

/**
 * Initializes Amplitude session replay when called. Requires AMPLITUDE_SESSION_REPLAY_API_KEY to be set.
 * Call when the session replay experiment is enabled (see SessionReplayInitializer).
 * Complies with Atlassian guide: conditional load, session ID linking, UGC filter rules for Heatmaps.
 */
export const initializeSessionReplay = async (): Promise<void> => {
	const apiKey = getSessionReplayAmplitudeApiKey();
	if (!apiKey) {
		return;
	}

	try {
		// Same async chunk as `onAnalyticsWebClientSessionRotation` / `SessionReplayInitializer` (see file comment above).
		const { init } = await import(
			/* webpackChunkName: "amplitude-session-replay" */ '@amplitude/session-replay-browser'
		);
		const deviceId = analyticsClient.getAnonymousId();
		const sessionId = analyticsClient.getCurrentSessionId();

		await init(apiKey, {
			deviceId,
			sessionId,
			/** Use IndexedDB so events persist when the user navigates away (e.g. to options); memory is lost on unload. */
			storeType: 'idb',
			optOut: false,
			applyBackgroundColorToBlockedElements: true,
			/** Record 100% of sessions when selected; without this the SDK defaults to sampling and opts sessions out */
			sampleRate: 1,
			/** In dev, level 4 (Debug) so you can see when replay data is sent in the console */
			logLevel: debugEnabled ? 4 : undefined,
			privacyConfig: {
				defaultMaskLevel: 'conservative',
				maskSelector: NTP_PRIVACY_MASK_SELECTORS,
				blockSelector: NTP_PRIVACY_BLOCK_SELECTORS,
			},
			interactionConfig: {
				enabled: true,
				batch: true,
				ugcFilterRules: UGC_FILTER_RULES,
			},
		}).promise;
	} catch (error) {
		logError({ error, impact: 'Failed to initialize session replay' });
	}
};

/**
 * Stops Amplitude session replay capture and tears down listeners. Safe to call when replay was never initialized.
 */
export const shutdownSessionReplay = async (): Promise<void> => {
	try {
		const { shutdown } = await import(
			/* webpackChunkName: "amplitude-session-replay" */ '@amplitude/session-replay-browser'
		);
		shutdown();
	} catch (error) {
		logError({ error, impact: 'Failed to shutdown session replay' });
	}
};
